import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import StandardModal from '../../../components/modales/StandardModal';
import type { PickedLocation } from '../../../components/maps/LocationPicker';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { AdminPage, FormActions, FormSection, FullRow, apiError } from '../shared/AdminUi';

// El mapa (MapLibre) se descarga solo al abrir este formulario.
const LocationPicker = React.lazy(() => import('../../../components/maps/LocationPicker'));

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface SharedClient {
  id: number;
  fullName: string;
  idNumber: string;
  route: { id: number; name: string | null };
}

type Field = 'officeId' | 'routeId' | 'idNumber' | 'fullName' | 'phone' | 'mobilePhone' | 'mainAddress' | 'job';

/** Crear cliente: sede y ruta, datos del cliente, ubicación y codeudor. */
const ClientNew: React.FC<ShowAlerts> = () => {
  const t = useT();
  const notify = useNotify();
  const navigate = useNavigate();
  const { canCreate } = useResourcePermissions('/admin/clients');
  const [idNumber, setIdNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [mainAddress, setMainAddress] = useState('');
  const [secondaryAddress, setSecondaryAddress] = useState('');
  const [landmark, setLandmark] = useState('');
  const [location, setLocation] = useState<PickedLocation | null>(null);
  const [job, setJob] = useState('');
  const [coSignerFullName, setCoSignerFullName] = useState('');
  const [coSignerIdNumber, setCoSignerIdNumber] = useState('');
  const [coSignerAddress, setCoSignerAddress] = useState('');
  const [coSignerPhone, setCoSignerPhone] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [saving, setSaving] = useState(false);
  // Cliente que ya existe en otra ruta de la sede (sede con clientes compartidos).
  const [existing, setExisting] = useState<SharedClient | null>(null);

  setPageTitle('clients.newTitle');

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const req = t('common.required');
    const found: Partial<Record<Field, string>> = {};
    if (!officeId) found.officeId = req;
    if (!routeId) found.routeId = req;
    if (!idNumber) found.idNumber = req;
    if (!fullName.trim()) found.fullName = req;
    if (!phone) found.phone = req;
    if (!mobilePhone) found.mobilePhone = req;
    if (!mainAddress.trim()) found.mainAddress = req;
    if (!job.trim()) found.job = req;
    setErrors(found);
    if (Object.keys(found).length) {
      notify.warning(t('clients.missingFields'));
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('/clients', {
        idNumber,
        fullName,
        phone,
        mobilePhone,
        mainAddress,
        secondaryAddress,
        landmark: landmark.trim() || null,
        job,
        coSignerFullName,
        coSignerIdNumber,
        coSignerAddress,
        coSignerPhone,
        officeId: Number(officeId),
        routeId: Number(routeId),
      });
      const newId = response?.data?.id;
      if (location && newId) {
        try {
          await axios.put(`/clients/${newId}/location`, location);
        } catch (locationError) {
          notify.warning(t('clients.location.saveError'));
        }
      }
      notify.success(t('clients.createdOk'));
      navigate('/admin/clients');
    } catch (error) {
      const body = axios.isAxiosError(error) ? (error.response?.data as { code?: string; client?: SharedClient }) : undefined;
      if (body?.code === 'CLIENT_EXISTS_IN_OFFICE' && body.client) {
        // La sede comparte clientes: no se crea uno repetido; se explica y se ofrece prestarle.
        setExisting(body.client);
      } else {
        notify.error(apiError(error, t('clients.createError')));
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [officesData, officeRoutesData] = await Promise.all([
          axios.get('offices'),
          officeId ? axios.get(`offices/${officeId}/routes`) : null,
        ]);
        if (officesData) setOffices(Array.isArray(officesData.data?.data) ? officesData.data.data : []);
        setOfficeRoutes(officeRoutesData && Array.isArray(officeRoutesData.data) ? officeRoutesData.data : []);
      } catch (error) {
        notify.error(t('common.loadError'));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId]);

  const err = (f: Field) => ({ error: !!errors[f], helperText: errors[f] });
  const digits = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => setter(e.target.value.replace(/\D/g, ''));

  return (
    <AdminPage narrow>
      <PageHeader
        title={t('clients.newTitle')}
        description={t('clients.newSubtitle')}
        breadcrumbs={[{ label: t('clients.title'), href: '#/admin/clients' }, { label: t('clients.newTitle') }]}
        helpAction={<ScreenTourButton />}
      />
      <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box data-tour="client-form-office">
          <FormSection title={t('clients.officeSection')}>
            <TextField
              id="client-office"
              select
              label={t('clients.office')}
              value={offices.length ? officeId : ''}
              onChange={(event) => {
                setOfficeId(event.target.value);
                setRouteId('');
              }}
              required
              fullWidth
              {...err('officeId')}
            >
              {offices.map((office) => (
                <MenuItem key={office.id} value={String(office.id)}>
                  {office.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="client-route"
              select
              label={t('clients.route')}
              value={officeRoutes.length ? routeId : ''}
              onChange={(event) => setRouteId(event.target.value)}
              disabled={!officeId}
              required
              fullWidth
              {...err('routeId')}
            >
              {officeRoutes.map((route) => (
                <MenuItem key={route.id} value={String(route.id)}>
                  {route.name}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>
        </Box>
        <Box data-tour="client-form-data">
          <FormSection title={t('clients.clientSection')}>
            <TextField id="idNumber" data-tour="client-form-id-number" label={t('clients.idNumber')} placeholder="143526799" value={idNumber} onChange={digits(setIdNumber)} inputProps={{ maxLength: 20, inputMode: 'numeric' }} required fullWidth {...err('idNumber')} />
            <TextField id="fullName" label={t('clients.fullName')} placeholder={t('clients.fullNamePlaceholder')} value={fullName} onChange={(e) => setFullName(e.target.value)} inputProps={{ maxLength: 30 }} required fullWidth {...err('fullName')} />
            <TextField id="mobilePhone" type="tel" label={t('common.cellphone')} placeholder="3101234567" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} inputProps={{ maxLength: 10 }} required fullWidth {...err('mobilePhone')} />
            <TextField id="phone" type="tel" label={t('common.phone')} placeholder="6011234567" value={phone} onChange={(e) => setPhone(e.target.value)} inputProps={{ maxLength: 10 }} required fullWidth {...err('phone')} />
            <TextField id="job" label={t('clients.job')} placeholder={t('clients.jobPlaceholder')} value={job} onChange={(e) => setJob(e.target.value)} inputProps={{ maxLength: 30 }} required fullWidth {...err('job')} />
          </FormSection>
        </Box>
        <Box data-tour="client-form-address">
          <FormSection title={t('clients.addressSection')}>
            <TextField id="mainAddress" label={t('common.address')} value={mainAddress} onChange={(e) => setMainAddress(e.target.value)} inputProps={{ maxLength: 30 }} required fullWidth {...err('mainAddress')} />
            <TextField id="secondaddres" label={t('clients.secondAddress')} value={secondaryAddress} onChange={(e) => setSecondaryAddress(e.target.value)} inputProps={{ maxLength: 30 }} fullWidth />
            <FullRow>
              <TextField
                id="landmark"
                label={t('clients.landmark')}
                placeholder={t('clients.landmarkPlaceholder')}
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
                inputProps={{ maxLength: 255 }}
                multiline
                minRows={2}
                fullWidth
              />
            </FullRow>
            <FullRow>
              <Box data-tour="client-form-location">
              <React.Suspense fallback={null}>
                <LocationPicker value={location} onChange={setLocation} />
              </React.Suspense>
              </Box>
            </FullRow>
          </FormSection>
        </Box>
        <Box data-tour="client-form-cosigner">
          <FormSection title={t('clients.coSignerSection')} description={t('clients.coSignerHint')}>
            <TextField id="coSignerFullName" label={t('clients.coSignerName')} placeholder={t('clients.coSignerNamePlaceholder')} value={coSignerFullName} onChange={(e) => setCoSignerFullName(e.target.value)} inputProps={{ maxLength: 30 }} fullWidth />
            <TextField id="coSignerIdNumber" label={t('clients.coSignerIdNumber')} placeholder="143526799" value={coSignerIdNumber} onChange={(e) => setCoSignerIdNumber(e.target.value)} inputProps={{ maxLength: 20 }} fullWidth />
            <TextField id="coSignerPhone" type="tel" label={t('clients.coSignerPhone')} value={coSignerPhone} onChange={(e) => setCoSignerPhone(e.target.value)} inputProps={{ maxLength: 10 }} fullWidth />
            <TextField id="coSignerAddress" label={t('clients.coSignerAddress')} value={coSignerAddress} onChange={(e) => setCoSignerAddress(e.target.value)} inputProps={{ maxLength: 30 }} fullWidth />
          </FormSection>
        </Box>
        <FormActions cancelTo="/admin/clients" saving={saving} allowed={canCreate} deniedTooltip={t('common.noPermissionCreate')} />
      </Box>

      <StandardModal
        open={Boolean(existing)}
        onClose={() => setExisting(null)}
        title={t('clients.shared.existsTitle')}
        subtitle={existing ? `${existing.fullName} · ${existing.idNumber}` : undefined}
        maxWidth={520}
        actions={
          <>
            <Button variant="outlined" color="inherit" onClick={() => setExisting(null)}>
              {t('common.close')}
            </Button>
            <Button variant="contained" onClick={() => navigate('/loans/new')}>
              {t('clients.shared.goLoan')}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('clients.shared.existsBody', { route: existing?.route?.name ?? '' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('clients.shared.existsHelp')}
        </Typography>
      </StandardModal>
    </AdminPage>
  );
};

export default ClientNew;
